// @flow
import * as React from 'react';
import { hot } from 'react-hot-loader';
import { SnackbarProvider } from 'notistack';
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import formatDate from 'date-fns/format';
import fr from 'date-fns/locale/fr';
import enUS from 'date-fns/locale/en-US';
import { StripeProvider } from 'react-stripe-elements';

import TranslationEn from '../assets/translation/en.json';
import TranslationFr from '../assets/translation/fr.json';
import Routes from './routes';
import { SiteThemeProvider } from './contexts/SiteThemeProvider';
import { langs } from './utils/i18n';
import { init } from './utils/log';
import { getUrlParams, isProd, getOS } from './utils';

init()
  // eslint-disable-next-line
  .then(console.log)
  // eslint-disable-next-line
  .catch(console.error);

i18n
  .use(initReactI18next) // passes i18n down to react-i18next
  .use(LanguageDetector)
  .init(
    {
      resources: {
        en: {
          translation: TranslationEn,
        },
        fr: {
          translation: TranslationFr,
        },
      },
      detection: {
        order: ['cookie', 'localStorage', 'navigator'],
        caches: ['localStorage', 'cookie'],
      },
      lngs: langs,
      fallbackLng: 'en',
      interpolation: {
        escapeValue: false,
        format: (value: any, format: any, lng: any) => {
          if (format === 'uppercase') {
            return value.toUpperCase();
          }
          if (format === 'number') {
            return value.toLocaleString(lng);
          }
          if (value instanceof Date) {
            return formatDate(value, format, { locale: lng === 'fr' ? fr : enUS });
          }
          return value;
        },
      },
    },
    (err, t) => {
      window.document.title = t('title');
    }
  );

const urlParams = getUrlParams();
if (urlParams.lang === 'fr') {
  i18n.changeLanguage('fr');
}

interface AppWrapperProps {
  children: React.Node;
}

// TODO: handling those keys should happen at the build layer. However, there
// is currently no way to disntiguish between stage and prod there - both are
// built with the same flag. So if we want to distinguish stage and prod, it
// needs to happen here. Refactor would be useful.
const STRIPE_EN_TEST = 'pk_test_daU2MJMJo4Ee163vkLxXCiqs';
const STRIPE_EN_PROD = 'pk_live_y0TZJeC97ssgM3xzW5Ck5JKo';
const STRIPE_FR_TEST = 'pk_test_qpXeb32F6QVJtkAZMZOhUqMF';
const STRIPE_FR_PROD = 'pk_live_ukr4gepggIK7orPDjPuVl7tu';

const AppWrapper = (props: AppWrapperProps) => {
  const { children } = props;
  const [stripeKey, setStripeKey] = React.useState();
  const updateStripeKey = (language: string) => {
    let key = STRIPE_EN_TEST;
    if (language && language.startsWith('en')) {
      key = isProd() ? STRIPE_EN_PROD : STRIPE_EN_TEST;
    } else if (language && language.startsWith('fr')) {
      key = isProd() ? STRIPE_FR_PROD : STRIPE_FR_TEST;
    }
    setStripeKey(key);
  };
  if (!stripeKey) updateStripeKey(i18n.language);
  React.useEffect(() => {
    const event = i18n.on('languageChanged', updateStripeKey);
    return () => i18n.off('languageChanged', event);
  }, []);
  return (
    <SiteThemeProvider>
      <StripeProvider apiKey={stripeKey} key={stripeKey}>
        <SnackbarProvider
          preventDuplicate
          iconVariant={{
            warning: '',
          }}
          maxSnack={3}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          autoHideDuration={5000}
          classes={{
            root: 'a-snackbar',
          }}
          disableWindowBlurListener
        >
          {children}
        </SnackbarProvider>
      </StripeProvider>
    </SiteThemeProvider>
  );
};

interface AppProps {
  store: Object;
}

const App = (props: AppProps) => {
  const { store } = props;
  return <Routes AppWrapper={AppWrapper} store={store} />;
};

// $FlowFixMe
export default hot(module)(App);
